import { Controller } from "stimulus";
import Engine from "../recording/engine";

const BORDER_CLASSES = {
  idle: "border-gray-800",
  requesting: "border-gray-800",
  recording: "border-red-900/40",
  paused: "border-orange-900/30",
  uploading: "border-gray-800",
  complete: "border-gray-800",
  error: "border-red-900/40"
};

const TIMER_CLASSES = {
  idle: "text-gray-600",
  requesting: "text-gray-600",
  recording: "text-gray-200",
  paused: "text-gray-200",
  uploading: "text-gray-400",
  complete: "text-gray-600",
  error: "text-gray-400"
};

const WAVEFORM_COLORS = {
  idle: "bg-gray-700",
  requesting: "bg-gray-700",
  recording: "bg-red-500/70",
  paused: "bg-orange-500/40",
  complete: "bg-gray-700"
};

const ALL_BORDER_CLASSES = [...new Set(Object.values(BORDER_CLASSES))];
const ALL_TIMER_CLASSES = [...new Set(Object.values(TIMER_CLASSES))];
const ALL_WAVEFORM_COLORS = [...new Set(Object.values(WAVEFORM_COLORS))];

export default class extends Controller {
  static targets = [
    "container",
    "indicator",
    "timer",
    "waveform",
    "startButton",
    "pauseButton",
    "resumeButton",
    "stopButton",
    "retryButton",
    "progressContainer",
    "progressBar",
    "uploadPercent",
    "error",
    "form",
    "fileInput",
    "recovery",
    "recoveryDate",
    "recoveryDuration"
  ];

  connect() {
    this.engine = new Engine({
      onStateChange: this.onStateChange,
      store: this.element.dataset.recorderStore
    });
    this.checkForRecovery();
  }

  disconnect() {
    this.engine.destroy();
  }

  // Actions

  async start() {
    await this.engine.start();
  }

  async pause() {
    await this.engine.pause();
  }

  async resume() {
    await this.engine.resume();
  }

  async stop() {
    const file = await this.engine.stop();
    if (file) this.attachFileAndSubmit(file);
  }

  retryUpload() {
    this.engine.retryUpload();
    this.attachFileAndSubmit(this.engine.file);
  }

  // Direct Upload + Turbo event handlers

  onUploadProgress(event) {
    this.engine.uploadProgress(event.detail.progress);
  }

  onUploadError(event) {
    event.preventDefault();
    this.engine.uploadFailed("Upload failed. You can retry or the recording will be lost if you leave.");
  }

  async onFormSubmitEnd(event) {
    if (event.detail.success) {
      await this.engine.uploadComplete();
      this.resetForm();
      this.checkForRecovery();
    } else {
      this.engine.uploadFailed("Failed to save recording. You can retry.");
    }
  }

  // Engine callback — single render path

  onStateChange = (state) => {
    const status = state.status;
    this.renderContainer(status);
    this.renderIndicator(status);
    this.renderTimer(state);
    this.renderWaveform(status);
    this.renderControls(status);
    this.renderProgress(state);
    this.renderError(state);
  };

  // Recovery

  async checkForRecovery() {
    const session = await this.engine.checkForRecovery();
    if (!session) return;

    this.recoverySession = session;
    this.recoveryDateTarget.textContent = this.formatRecoveryDate(session.startedAt);
    this.recoveryDurationTarget.textContent = await session.duration();
    this.recoveryTarget.classList.remove("hidden");
  }

  async recoverUpload() {
    this.recoveryTarget.classList.add("hidden");
    const file = await this.engine.recover(this.recoverySession);
    this.recoverySession = null;
    if (file) this.attachFileAndSubmit(file);
  }

  async discardRecovery() {
    await this.engine.discardRecovery(this.recoverySession);
    this.recoverySession = null;
    this.recoveryTarget.classList.add("hidden");
    this.checkForRecovery();
  }

  preventUnload(event) {
    if (this.active) event.preventDefault();
  }

  preventNavigation(event) {
    if (this.active && !confirm("A recording is in progress. Leave this page?")) event.preventDefault();
  }

  get active() {
    const status = this.engine.state.status;
    return status !== "idle" && status !== "complete";
  }

  // Private

  formatRecoveryDate(isoString) {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
    });
  }

  attachFileAndSubmit(file) {
    this.resetForm();
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.fileInputTarget.files = dataTransfer.files;
    this.formTarget.requestSubmit();
  }

  resetForm() {
    this.formTarget.querySelectorAll("input[type=hidden][name='meeting_recording[file]']").forEach(el => el.remove());
    this.fileInputTarget.disabled = false;
    this.fileInputTarget.value = "";
  }

  renderContainer(status) {
    const displayStatus = status === "complete" ? "idle" : status;
    const nextClass = BORDER_CLASSES[displayStatus] || BORDER_CLASSES.idle;
    ALL_BORDER_CLASSES.forEach(cls => this.containerTarget.classList.remove(cls));
    this.containerTarget.classList.add(nextClass);
  }

  renderIndicator(status) {
    const displayStatus = status === "complete" ? "idle" : status;
    this.indicatorTarget.querySelectorAll("[data-recording-state]").forEach(el => {
      this.toggle(el, el.dataset.recordingState === displayStatus);
    });
  }

  renderTimer(state) {
    const status = state.status;
    const displayStatus = status === "complete" ? "idle" : status;

    if (status === "complete") {
      this.timerTarget.textContent = "00:00:00";
    } else {
      this.timerTarget.textContent = state.elapsed;
    }

    const nextClass = TIMER_CLASSES[displayStatus] || TIMER_CLASSES.idle;
    ALL_TIMER_CLASSES.forEach(cls => this.timerTarget.classList.remove(cls));
    this.timerTarget.classList.add(nextClass);
  }

  renderWaveform(status) {
    const displayStatus = status === "complete" ? "idle" : status;
    const color = WAVEFORM_COLORS[displayStatus];
    const animate = displayStatus === "recording";

    if (color) {
      this.waveformTarget.classList.remove("hidden");
      const bars = this.waveformTarget.children;
      for (let i = 0; i < bars.length; i++) {
        ALL_WAVEFORM_COLORS.forEach(cls => bars[i].classList.remove(cls));
        bars[i].classList.add(color);
        bars[i].classList.toggle("animate-recording-waveform", animate);
        bars[i].style.animationDelay = animate ? bars[i].dataset.delay : "";
      }
    } else {
      this.waveformTarget.classList.add("hidden");
    }
  }

  renderControls(status) {
    const displayStatus = status === "complete" ? "idle" : status;

    const visibility = {
      idle:                    { start: true,  pause: false, resume: false, stop: false, retry: false },
      requesting:              { start: true,  pause: false, resume: false, stop: false, retry: false },
      recording:               { start: false, pause: true,  resume: false, stop: true,  retry: false },
      paused:                  { start: false, pause: false, resume: true,  stop: true,  retry: false },
      uploading:               { start: false, pause: false, resume: false, stop: false, retry: false },
      error:                   { start: true,  pause: false, resume: false, stop: false, retry: true  }
    }[displayStatus] || { start: true, pause: false, resume: false, stop: false, retry: false };

    this.toggle(this.startButtonTarget, visibility.start);
    this.toggle(this.pauseButtonTarget, visibility.pause);
    this.toggle(this.resumeButtonTarget, visibility.resume);
    this.toggle(this.stopButtonTarget, visibility.stop);
    this.toggle(this.retryButtonTarget, visibility.retry);
  }

  renderProgress(state) {
    if (state.status === "uploading" && state.progress !== null) {
      this.progressContainerTarget.classList.remove("hidden");
      this.progressBarTarget.style.width = `${state.progress}%`;
      this.uploadPercentTarget.textContent = `${Math.round(state.progress)}%`;
    } else {
      this.progressContainerTarget.classList.add("hidden");
    }
  }

  renderError(state) {
    if (state.message) {
      this.errorTarget.textContent = state.message;
      this.errorTarget.classList.remove("hidden");
    } else {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }

  toggle(element, show) {
    element.classList.toggle("hidden", !show);
  }

}
